import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AppNotification,
  FcmPayload,
  NotificationResponse,
  NotificationStore,
  NotificationType,
} from './models/notifications.model';
import { Preferences } from '@capacitor/preferences';
import { environment } from '@staffler/env';
import { AuthStore } from '@staffler/app/core';

const STORAGE_KEY = 'ms_notifications';
const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class NotificationService {
  readonly #NOTIFICATIONS_API_URL = environment.notificationsApiBaseUrl;
  readonly #http = inject(HttpClient);
  readonly #authStore = inject(AuthStore);
  readonly #all = signal<AppNotification[]>([]);

  constructor() {
    this.#authStore.cleared$.subscribe(() => {
      void this.clear().catch(err =>
        console.error('[NotificationService] clearing notifications on teardown failed:', err)
      );
    });
  }

  subscribe(
    userId: string,
    companyId: string | undefined,
    deviceToken: string
  ): Observable<NotificationResponse> {
    return this.#http.post<NotificationResponse>(`${this.#NOTIFICATIONS_API_URL}/subscribe`, {
      userId,
      companyId,
      deviceToken,
    });
  }

  unsubscribe(userId: string): Observable<void> {
    return this.#http.delete<void>(`${this.#NOTIFICATIONS_API_URL}/unsubscribe/${userId}`);
  }

  readonly notifications = computed(() =>
    this.#all().filter(n => Date.now() - new Date(n.sentAt).getTime() <= TWO_WEEKS_MS)
  );

  readonly unreadCount = computed(() => this.notifications().filter(n => !n.readAt).length);

  readonly newNotifications = computed(() =>
    this.notifications()
      .filter(n => !n.readAt)
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
  );

  readonly olderNotifications = computed(() =>
    this.notifications()
      .filter(n => !!n.readAt)
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
  );

  async load(): Promise<void> {
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (!value) return;
    try {
      const store: NotificationStore = JSON.parse(value);
      this.#all.set(store.notifications ?? []);
      await this.#pruneOld();
    } catch {
      await Preferences.remove({ key: STORAGE_KEY });
    }
  }

  async clear(): Promise<void> {
    this.#all.set([]);
    await Preferences.remove({ key: STORAGE_KEY });
  }

  async addFromFcm(payload: FcmPayload): Promise<void> {
    const notif: AppNotification = {
      id: payload.id ?? crypto.randomUUID(),
      title: payload.title,
      body: payload.body,
      type: (payload.data?.notificationType as NotificationType) ?? 'GENERAL',
      contractId: payload.data?.contractId,
      multipleDayContract: payload.data?.multipleDayContract === 'true',
      sentAt: payload.data?.notificationDate
        ? new Date(payload.data.notificationDate).toISOString()
        : new Date().toISOString(),
    };

    if (this.#all().some(n => n.id === notif.id)) return;
    this.#all.update(list => [notif, ...list]);
    await this.#persist();
  }

  async markAsRead(id: string): Promise<void> {
    this.#all.update(list =>
      list.map(n => (n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n))
    );
    await this.#persist();
  }

  async markAllAsRead(): Promise<void> {
    const now = new Date().toISOString();
    this.#all.update(list => list.map(n => (!n.readAt ? { ...n, readAt: now } : n)));
    await this.#persist();
  }

  async #pruneOld(): Promise<void> {
    const before = this.#all().length;
    this.#all.update(list =>
      list.filter(n => Date.now() - new Date(n.sentAt).getTime() <= TWO_WEEKS_MS)
    );
    if (this.#all().length !== before) await this.#persist();
  }

  async #persist(): Promise<void> {
    const store: NotificationStore = {
      notifications: this.#all(),
      lastUpdated: new Date().toISOString(),
    };
    await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(store) });
  }
}
